import { Grid, Stack } from '@mui/material';
import { useEventCallback } from '@mui/material/utils';
import * as IBAN from 'iban';
import { pick } from 'lodash';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckboxRadioItem } from '@work4all/components/lib/input/checkbox-radio-item';
import { LabeledInput } from '@work4all/components/lib/input/labeled-input';
import { useHistoryStack } from '@work4all/components/lib/navigation/history-stack';
import {
  EventType,
  sendAmplitudeData,
} from '@work4all/components/lib/utils/amplitude/amplitude';

import { useDataMutation } from '@work4all/data';

import { TenantBankAccount } from '@work4all/models/lib/Classes/TenantBankAccount.entity';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { PathsOf } from '@work4all/utils/lib/paths-of/paths-of';

import { LedgerAccountPickerField } from '../../../../../components/entity-picker/LedgerAccountPickerField';
import { ControllerPlus } from '../../../form-plus/controller-plus';
import { useFormContextPlus } from '../../../form-plus/use-form-context-plus';
import { Collapse } from '../../components';
import { useMaskConfig } from '../../hooks/mask-context';
import { OverlayController } from '../../overlay-controller/OverlayController';
import { useMaskOverlay } from '../../overlay-controller/use-mask-overlay';
import { MaskControllerProps } from '../../types';
import { useFormUpdate } from '../../utils/use-form-update';

import { useTenantBankAccountRequestData } from './hooks/use-tenant-bank-account-request-data';

type TenantBankAccountFormValue = PathsOf<TenantBankAccount, 2>;

type TenantBankAccountOverlayControllerProps = MaskControllerProps & {
  amplitudeEntryPoint: string;
};

const DEFAULT_VALUES = {};

export function TenantBankAccountOverlayController(
  props: TenantBankAccountOverlayControllerProps
) {
  const { amplitudeEntryPoint } = props;

  const { goBack, close, currentStackIndex, setObjectionListener } =
    useHistoryStack();

  const [mutate] = useDataMutation<TenantBankAccount, EMode.upsert>({
    entity: Entities.tenantBankAccount,
    mutationType: EMode.upsert,
    responseData: {
      id: null,
    },
  });

  const mask = useMaskConfig(props);

  useEffect(() => {
    if (mask.isCreateMode) {
      sendAmplitudeData(EventType.AddTenantBankAccount, {
        entryPoint: amplitudeEntryPoint,
      });
    } else {
      sendAmplitudeData(EventType.EditTenantBankAccount, {
        entryPoint: amplitudeEntryPoint,
      });
    }
  }, [amplitudeEntryPoint, mask, mask.isCreateMode, props.id]);

  const request = useTenantBankAccountRequestData(mask.id);

  const { t } = useTranslation();

  const customRules = useCallback(
    (data: TenantBankAccountFormValue) => {
      if (data.iban.length > 0 && !IBAN.isValid(data.iban)) {
        return {
          iban: {
            message: t('ERROR.IBAN_INVALID'),
            type: 'customValidation',
          },
        };
      }
      return true;
    },
    [t]
  );

  const overlay = useMaskOverlay<TenantBankAccountFormValue>({
    ...props,
    request,
    newEntityData: DEFAULT_VALUES,
    mask,
    getSubTitle: (x) => x.name,
    id: Number(mask.id),
    customRules,
  });

  const { formState } = overlay.form;

  const handleSubmit = useEventCallback(
    async (values: TenantBankAccountFormValue) => {
      const update = mask.isCreateMode
        ? values
        : pick(values, ['id', ...Object.keys(formState.dirtyFields)]);

      await mutate(update);

      setObjectionListener(null);
      sendAmplitudeData(EventType.SaveTenantBankAccount, {
        entryPoint: amplitudeEntryPoint,
      });
      if (currentStackIndex !== 0) {
        goBack();
      } else {
        close();
      }
    }
  );

  useFormUpdate(
    {
      ledgerAccount: (val) => {
        return { ledgerAccountId: val?.id || null };
      },
    },
    overlay.form
  );

  return (
    <OverlayController<TenantBankAccountFormValue>
      {...overlay}
      onSubmit={handleSubmit}
    >
      <TenantBankAccountMaskContent />
    </OverlayController>
  );
}

function TenantBankAccountMaskContent() {
  const { t } = useTranslation();

  const { register, control } =
    useFormContextPlus<TenantBankAccountFormValue>();

  return (
    <>
      <Collapse title={t('COMMON.MANDATORY_FIELD', { count: 2 })} defaultOpen>
        <Stack gap="1rem">
          <Grid container spacing="1rem">
            <Grid item xs={12} sm={6} md={4}>
              <LabeledInput label={t('COMMON.IBAN')} {...register('iban')} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <LabeledInput
                label={t('COMMON.ACCOUNT_OWNER')}
                {...register('accountOwner')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <LabeledInput
                label={t('COMMON.SEPA_CREDITOR_ID')}
                {...register('sEPACreditorId')}
              />
            </Grid>
          </Grid>
        </Stack>
      </Collapse>
      <Collapse title={t('COMMON.OPTIONAL_FIELD', { count: 2 })} defaultOpen>
        <Stack gap="1rem">
          <Grid container spacing="1rem">
            <Grid item xs={12} sm={6} md={4}>
              <LabeledInput label={t('COMMON.BIC')} {...register('bic')} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <LabeledInput label={t('COMMON.NAME')} {...register('name')} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <CheckboxRadioItem
                control="checkbox"
                label={t('TENANT_BANK_ACCOUNT.MAIN_ACCOUNT')}
                {...register('mainAccount')}
              />
            </Grid>
          </Grid>
        </Stack>
      </Collapse>
      <Collapse title={t('COMMON.ASSIGNMENT')} defaultOpen>
        <Stack gap="1rem">
          <Grid container spacing="1rem">
            <Grid item xs={12} sm={6} md={4}>
              <ControllerPlus
                control={control}
                name="ledgerAccount"
                render={({ field }) => {
                  return <LedgerAccountPickerField {...field} clearable />;
                }}
              />
            </Grid>
          </Grid>
        </Stack>
      </Collapse>
    </>
  );
}
